import { useCallback, useMemo } from 'react';

const HUBSPOT_ID = 25639734;
const scriptSrc = `//js-eu1.hs-scripts.com/${HUBSPOT_ID}.js`;
const HUBSPOT_ENABLED = import.meta.env.MODE !== 'development';

interface Window {
  _hsq?: Array<[string, ...unknown[]]>;
  HubSpotConversations?: {
    widget: {
      remove: () => void;
      hide: () => void;
    };
  };
}

function loadHubSpotScript() {
  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
}

export function useHubspot() {
  const init = useCallback(() => {
    if (HUBSPOT_ENABLED && !(window as Window)._hsq) {
      loadHubSpotScript();
    }
  }, []);

  const remove = useCallback(() => {
    const script = document.querySelector(`script[src="${scriptSrc}"]`);

    if (script) {
      script.remove();
    }

    // `_hsq` can be a non-configurable property (e.g. stubbed by a privacy
    // browser extension), in which case `delete` throws a TypeError in strict
    // mode. Cleaning up an analytics global must never crash the app, so try to
    // delete, fall back to clearing the value, and tolerate it being locked.
    if ((window as Window)._hsq) {
      try {
        delete (window as Window)._hsq;
      } catch {
        try {
          (window as Window)._hsq = undefined;
        } catch {
          // Property is locked down by an extension — nothing more we can do.
        }
      }
    }
  }, []);

  const identify = useCallback((email: string) => {
    const hubspot = (window as Window)._hsq;
    if (hubspot) {
      hubspot.push([
        'identify',
        {
          email,
        },
      ]);
    }
  }, []);

  const hubspot = useMemo(() => {
    return {
      init,
      remove,
      identify,
    };
  }, [init, remove, identify]);

  return hubspot;
}
